/**
 * Formats a number as a dollar-denominated string with customizable decimal places, thousands separator, and prefix symbol. Returns an empty string on error.
 *
 * @param amount - The dollar amount to format.
 * @param decimalCount - Number of decimal places. Defaults to 2.
 * @param decimal - The decimal separator character. Defaults to '.'.
 * @param thousands - The thousands separator character. Defaults to ','.
 */
export function formatDollarAmount(amount: number, decimalCount = 2, decimal = ".", thousands = ","): string {
    try {
        const decimals = isNaN(Math.abs(decimalCount)) ? 2 : Math.abs(decimalCount);
        const negativeSign = amount < 0 ? "-" : "";
        const absoluteAmount = Math.abs(amount);
        const integerPart = Math.floor(absoluteAmount);
        const integer = integerPart.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + thousands);
        const numericFraction = absoluteAmount.toFixed(decimals).split(".")[1] ?? "";
        const fractionalPart = numericFraction.length > 0 ? decimal + numericFraction : "";
        return "$" + negativeSign + integer + fractionalPart;
    } catch (e) {
        console.error(e);
        return "";
    }
}

/**
 * Adds thousands separators (commas) to a numeric value, converting it to a locale-independent string.
 *
 * @param input - The number or numeric string to format. Converted to string internally.
 * @returns The input as a string with commas inserted as thousands separators.
 *
 * @example
 * addThousandsSeparator(1234567); // '1,234,567'
 * addThousandsSeparator('12345'); // '12,345'
 * addThousandsSeparator(100); // '100'
 */
export const addThousandsSeparator = (input: number | string) => {
    const inputStr = typeof input === 'number' ? input.toString() : input;
    const formattedString = inputStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return formattedString;
};

/**
 * Truncates a decimal string (e.g. the fractional part of a number) to a specified number of digits, returning it with a leading dot.
 *
 * @param decimalLimit - Maximum number of decimal digits to keep. If 0, returns an empty string.
 * @param decimalString - The decimal digits as a string. Returns '' if undefined.
 *
 * @example
 * formatDecimalString(2, '12345'); // '.12'
 * formatDecimalString(0, '12345'); // ''
 * formatDecimalString(3, undefined); // ''
 */
export const formatDecimalString = (decimalLimit: number, decimalString: string | undefined) =>
    decimalString === undefined ? '' : decimalLimit > 0 ? `.${decimalString.slice(0, decimalLimit)}` : '';

/**
 * Validates that a number's string representation has at most the given number of decimal places.
 *
 * @param value - Default 0. The numeric value to validate (coerced to string for matching).
 * @param decimalPlaces - The maximum allowed decimal place count in the string representation.
 *
 * @example
 * validateDecimalPlaces(0, 2, '123.45'); // true
 * validateDecimalPlaces(0, 2, '123.456'); // false
 * validateDecimalPlaces(0, 0, 'abc'); // false
 */
export const validateDecimalPlaces = (value: number = 0, decimalPlaces: number) =>
    new RegExp(`^\\d+(\\.\\d{1,${decimalPlaces}})?$`).test(value.toString());

/**
 * Formats a number as an abbreviated string with a symbol prefix, using lowercase suffixes 'k' (thousands) or 'm' (millions).
 *
 * @param value - The numeric value to abbreviate.
 * @param symbol - A prefix symbol prepended before the number (e.g. '$').
 *
 * @example
 * formatAbbreviatedValue(1500000, '$'); // '$1.5m'
 * formatAbbreviatedValue(5000, '$'); // '$5.0k'
 * formatAbbreviatedValue(500, '$'); // '$500'
 */
export const formatAbbreviatedValue = (value: number, symbol: string) =>
    value >= 1e6 ? `${symbol}${(value / 1e6).toFixed(1)}m`
        : value >= 1e3 ? `${symbol}${(value / 1e3).toFixed(1)}k`
            : `${symbol}${value}`;

/**
 * Formats a number in thousands, returning a string with a symbol prefix, two decimals, and a 'k' suffix.
 *
 * @param value - The numeric value to format. Returns '' for null/NaN.
 * @param symbol - Optional prefix symbol (e.g. '$'). Defaults to empty string.
 *
 * @example
 * formatAbbreviatedValueToThousands(1234, '$'); // '$1.23k'
 * formatAbbreviatedValueToThousands(500, '$'); // '$0.50k'
 * formatAbbreviatedValueToThousands(1500000, '$'); // '$1500.00k'
 */
export const formatAbbreviatedValueToThousands = (
  value: number,
  symbol: string = ''
) => {
  if (value == null || isNaN(value as any)) return '';
  const sign = value < 0 ? '-' : '';
  const inThousands = Math.abs(value) / 1000;
  return `${sign}${symbol}${inThousands.toFixed(2)}k`;
};

/**
 * Formats a dollar amount with abbreviated suffixes ('m' for millions, optionally 'k' for thousands) based on magnitude. Values under 1000 render fully with decimals. Returns '' for null/NaN.
 *
 * @param value - The dollar amount to abbreviate.
 * @param decimals - Options object with configurable decimal count (default 2) and whether to use 'k' for thousands (default false — only 'm' for millions).
 *
 * @example
 * abbreviateCurrency(1500000, {decimals: 2}); // '$1.50m'
 * abbreviateCurrency(500000, {decimals: 0}); // '$0.5m'
 * abbreviateCurrency(500, {decimals: 2}); // '$500.00'
 * abbreviateCurrency(-1000000, {decimals: 1, useK: true}); // '-$1.0m'
 */
export function abbreviateCurrency(
  value: number,
  { decimals = 2, useK = false }: { decimals?: number; useK?: boolean } = {}
): string {
    if (value == null || isNaN(value as any)) return '';

    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (abs >= 1e6) {
        return `${sign}$${(abs / 1e6).toFixed(decimals)}m`;
    }

    if (useK && abs >= 1e3) {
        return `${sign}$${(abs / 1e3).toFixed(decimals)}k`;
    }

    return `${sign}$${abs.toFixed(decimals)}`;
}


/**
 * Formats a number as a dollar-denominated string in millions, using `toLocaleString` for locale-aware number formatting. Does not add a lowercase suffix (e.g., no 'M') — just the symbol prefix with the formatted value.
 *
 * @param value - The dollar amount to convert to millions.
 * @param decimals - Number of decimal places for the output. Defaults to 2.
 *
 * @example
 * abbreviateCurrencyToMillions(1500000, 2); // '$1.50'
 * abbreviateCurrencyToMillions(100, 0); // '$0'
 * abbreviateCurrencyToMillions(null as any, 2); // ''
 */
export function abbreviateCurrencyToMillions(
  value: number,
  decimals: number = 2
): string {
  if (value == null || isNaN(value as any)) return '';
  const sign = value < 0 ? '-' : '';
  return `${sign}$${(Math.abs(value) / 1_000_000).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Formats a decimal value as a percentage string relative to a total, with optional decimal place control. Trims trailing zeros after the decimal point (e.g. '50.00%' becomes '50%').
 *
 * @param value - The numerator value (typically between 0 and total).
 * @param total - The denominator/total. Returns '0%' if falsy or NaN.
 * @param decimals - Decimal places for the percentage string. Defaults to 0.
 *
 * @example
 * formatPercent(50, 100, 0); // '50%'
 * formatPercent(50, 100, 2); // '50%'
 * formatPercent(33.5, 100, 1); // '33.5%'
 */
export function formatPercent(value: number, total: number, decimals = 0): string {
    if (!total || isNaN(total)) return '0%';
    const out = ((value / total) * 100).toFixed(decimals).replace(/\.0+$/, '');
    return `${out}%`;
}